import { type FC, type ReactNode, type SVGProps, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { type PimProductV11 } from '@redteclab/api/clients/bully'
import {
  BrandIconAddress,
  BrandIconBarcode,
  BrandIconBrand,
  BrandIconCapsule,
} from '@redteclab/brand-icons'

import { useGlobalConfigContext } from '../../../global-config'
import { TENANT } from '../../../tenant'
import { urlNormalizeUri } from '../../../url-handling'
import {
  productDataAccessorGetDisplayCode,
  productDataAccessorGetEan,
  productDataAccessorGetManufacturer,
  productDataAccessorGetPharmaForm,
  productDataAccessorGetPzn,
} from '../../model/productDataAccessor'
import { useProductPageContext } from '../productPageContext'

import { ProductKeyFactBarcodeInfo } from './ProductKeyFactBarcodeInfo'

type ProductPageKeyFactType =
  'brand' | 'manufacturer' | 'pharmaForm' | 'pznAndEan'

export interface ProductPageKeyFact {
  content: ReactNode
  contentUrl?: string
  icon?: FC<SVGProps<SVGSVGElement>>
  title: string
  type: ProductPageKeyFactType
}

const useGetManufacturer = (
  product: PimProductV11,
): ProductPageKeyFact | undefined => {
  const intl = useIntl()
  const brand = productDataAccessorGetManufacturer(product)
  if (!brand) {
    return undefined
  }

  return {
    content: brand,
    icon: BrandIconAddress,
    title: intl.formatMessage({ id: 'general.label.manufacturer' }),
    type: 'manufacturer',
  }
}

const useGetPznAndEan = (
  product: PimProductV11,
): ProductPageKeyFact | undefined => {
  const intl = useIntl()
  const { tenant } = useGlobalConfigContext()
  const code =
    productDataAccessorGetDisplayCode(product) ??
    productDataAccessorGetPzn(product)
  const ean = productDataAccessorGetEan(product)
  const showExplanationIcon = new Set<TENANT>([TENANT.AT, TENANT.COM]).has(
    tenant,
  )

  if (code && ean) {
    return {
      content: (
        <ProductKeyFactBarcodeInfo
          content={`${code} / ${ean}`}
          showExplanationIcon={showExplanationIcon}
        />
      ),
      icon: BrandIconBarcode,
      title: `${intl.formatMessage({
        id: 'general.label.pzn',
      })} / ${intl.formatMessage({ id: 'general.label.ean' })}`,
      type: 'pznAndEan',
    }
  }

  if (code) {
    return {
      content: (
        <ProductKeyFactBarcodeInfo
          content={code}
          showExplanationIcon={showExplanationIcon}
        />
      ),
      icon: BrandIconBarcode,
      title: intl.formatMessage({
        id: 'general.label.pzn',
      }),
      type: 'pznAndEan',
    }
  }

  if (ean) {
    return {
      content: (
        <ProductKeyFactBarcodeInfo
          content={ean}
          showExplanationIcon={showExplanationIcon}
        />
      ),
      icon: BrandIconBarcode,
      title: intl.formatMessage({
        id: 'general.label.ean',
      }),
      type: 'pznAndEan',
    }
  }

  return undefined
}

const useGetPharmaForm = (
  product: PimProductV11,
): ProductPageKeyFact | undefined => {
  const intl = useIntl()

  const pharmaForm = productDataAccessorGetPharmaForm(product)

  return {
    content: pharmaForm,
    icon: BrandIconCapsule,
    title: intl.formatMessage({ id: 'general.label.pharmaceuticalForm' }),
    type: 'pharmaForm',
  }
}

const useGetBrand = (
  product: PimProductV11,
): ProductPageKeyFact | undefined => {
  const intl = useIntl()
  const { locale } = useGlobalConfigContext()

  const { brand } = product

  if (!brand) {
    return undefined
  }

  return {
    content: brand.name,
    contentUrl: brand.path && urlNormalizeUri({ locale, uri: brand.path }),
    icon: BrandIconBrand,
    title: intl.formatMessage({ id: 'general.label.brand' }),
    type: 'brand',
  }
}

export const useProductPageKeyFacts = (): ProductPageKeyFact[] => {
  const { currentVariant } = useProductPageContext()
  const manufacturer = useGetManufacturer(currentVariant)
  const pznAndEan = useGetPznAndEan(currentVariant)
  const pharmaForm = useGetPharmaForm(currentVariant)
  const brand = useGetBrand(currentVariant)

  return useMemo(
    () =>
      [pznAndEan, pharmaForm, brand, manufacturer].filter(
        (keyFact): keyFact is ProductPageKeyFact => keyFact !== undefined,
      ),
    [brand, pznAndEan, manufacturer, pharmaForm],
  )
}
